import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Clock, RefreshCw } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TimeRangeInput, type TimeRangeInputValue, computesCrossesMidnight } from '@/components/TimeRangeInput';
import { DOW_LABELS } from '@/lib/calendar-day';
import type { NodesResponse } from '@/stores/scope';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/settings/standing-pattern-templates` (v0.4 追加要件⑫ 増分③、D-379)。
 * 設計: docs/design/v04-standing-pattern-master-detail.md §3-1 / §6
 *
 * 恒常勤務パターンの名前付きマスタ。`/shift/standing-patterns` (店舗の週間カバレッジビュー) から
 * 「テンプレートから展開」する元になる。standing_availability_patterns (0048) と同型で
 * 1曜日に複数コマを許容する (default_work_pattern_entries とは異なる、migration 0113 参照)。
 */

const NULL_NODE_VALUE = '__tenant_wide__';
const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';

export type Template = { template_id: string; code: string; label: string; node_id: string | null; sort_order: number };
// §8-221対策: preference_level_label/is_available はサーバーが解決した値 (廃止済みの選好レベルでも
// 正しく表示できる、routes/standing-pattern-templates.ts serializeTemplateEntry 参照)。
export type TemplateEntry = {
  day_of_week: number; start_time: string; end_time: string; crosses_midnight: boolean; preference_level_id: string;
  preference_level_label?: string | null; preference_level_is_available?: boolean | null;
};
type PreferenceLevel = { level_id: string; code: string; label: string; is_available: boolean; weight: number; sort_order: number };

const entryKey = (e: { day_of_week: number; start_time: string }) => `${e.day_of_week}|${e.start_time}`;

function EntryEditorModal({
  day, entry, levels, loading, onClose, onSave, onRemove,
}: {
  day: number; entry: TemplateEntry | null; levels: PreferenceLevel[]; loading: boolean;
  onClose: () => void; onSave: (value: TimeRangeInputValue, levelId: string) => void; onRemove: (() => void) | null;
}) {
  const [value, setValue] = useState<TimeRangeInputValue>({
    startTime: entry?.start_time ?? '09:00', endTime: entry?.end_time ?? '17:00',
  });
  const [levelId, setLevelId] = useState(entry?.preference_level_id ?? levels[0]?.level_id ?? '');
  const canSave = !!value.startTime && !!value.endTime && !!levelId;

  return (
    <Modal open title={`${DOW_LABELS[day]}曜日の枠`} onClose={onClose} size="sm"
      footer={(
        <>
          {onRemove && <Button variant="ghost" onClick={onRemove} loading={loading}>この枠を削除</Button>}
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSave || loading} loading={loading} onClick={() => onSave(value, levelId)}>保存</Button>
        </>
      )}>
      <div className="space-y-4">
        <TimeRangeInput value={value} onChange={setValue} />
        <div>
          <label className="block text-sm text-text-sub mb-1">選好レベル</label>
          <Select value={levelId || undefined} onValueChange={setLevelId}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
            <SelectContent>
              {levels.map((l) => <SelectItem key={l.level_id} value={l.level_id}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  );
}

/** ⚠️ DayRow (default-work-patterns) と同じく、このパネル自体は Modal を開かない — 展開中の
 *  各枠が独自の EntryEditorModal を開くため、パネル自体を Modal 化すると「Modal の中で Modal を
 *  開く」構造になり Escape キーの二重発火を招く (docs/known-pitfalls.md 該当箇所参照)。 */
function EntriesPanel({ templateId, canManage }: { templateId: string; canManage: boolean }) {
  const qc = useQueryClient();
  const [editContext, setEditContext] = useState<{ day: number; entry: TemplateEntry | null } | null>(null);

  const { data, isLoading } = useQuery<{ entries: TemplateEntry[] }>({
    queryKey: ['standing-pattern-template-entries', templateId],
    queryFn: () => apiFetch(`/standing-pattern-templates/${templateId}/entries`),
  });
  const entries = data?.entries ?? [];
  const { data: levelsData } = useQuery<{ preference_levels: PreferenceLevel[] }>({ queryKey: ['preference-levels'], queryFn: () => apiFetch('/preference-levels') });
  const levels = levelsData?.preference_levels ?? [];
  const levelById = useMemo(() => new Map(levels.map((l) => [l.level_id, l])), [levels]);

  const saveMut = useMutation({
    mutationFn: (nextEntries: TemplateEntry[]) => apiFetch(`/standing-pattern-templates/${templateId}/entries`, {
      method: 'PUT', body: JSON.stringify({ entries: nextEntries }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['standing-pattern-template-entries', templateId] });
      toast.success('保存しました');
      setEditContext(null);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const applyEntry = (value: TimeRangeInputValue, levelId: string) => {
    if (!editContext) return;
    const crossesMidnight = computesCrossesMidnight(value);
    const newEntry: TemplateEntry = {
      day_of_week: editContext.day, start_time: value.startTime!, end_time: value.endTime!,
      crosses_midnight: crossesMidnight, preference_level_id: levelId,
    };
    const originalKey = editContext.entry ? entryKey(editContext.entry) : null;
    const next = originalKey
      ? entries.map((e) => (entryKey(e) === originalKey ? newEntry : e))
      : [...entries, newEntry];
    saveMut.mutate(next);
  };
  const removeEntry = () => {
    if (!editContext?.entry) return;
    const originalKey = entryKey(editContext.entry);
    saveMut.mutate(entries.filter((e) => entryKey(e) !== originalKey));
  };

  return (
    <div className="mt-2 pt-2 border-t border-surface-line">
      {isLoading ? (
        <div className="text-text-sub text-sm">読み込み中…</div>
      ) : (
        <ul className="space-y-2">
          {DOW_LABELS.map((label, day) => {
            const dayEntries = entries.filter((e) => e.day_of_week === day);
            return (
              <li key={day} className="flex items-start gap-2 border border-surface-line rounded p-2 text-sm">
                <span className="w-10 shrink-0 font-semibold text-text-body pt-2">{label}</span>
                <div className="flex-1 flex flex-wrap items-start gap-1.5">
                  {dayEntries.map((e) => {
                    // §8-221対策: サーバー解決済みのラベルを優先する (levelById は現行マスタのみ)。
                    // design-expert HIGH反映: 廃止済み(現行マスタに無い)ラベルは斜体+ツールチップで区別。
                    const level = levelById.get(e.preference_level_id);
                    const isHistorical = !level;
                    const label = e.preference_level_label ?? level?.label ?? '(削除済みの選好レベル)';
                    return (
                      <button key={entryKey(e)} type="button" disabled={!canManage} onClick={() => setEditContext({ day, entry: e })}
                        title={isHistorical ? `${label} (廃止済みの選好レベル)` : undefined}
                        className="text-xs px-2 py-1.5 min-h-[44px] rounded border border-surface-border bg-surface-sunken text-text-body text-left">
                        {e.start_time}〜{e.end_time}
                        <span className={`block text-text-sub ${isHistorical ? 'italic' : ''}`}>{label}</span>
                      </button>
                    );
                  })}
                  {canManage && (
                    <button type="button" aria-label={`${label}曜日に枠を追加`} onClick={() => setEditContext({ day, entry: null })}
                      className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-brand-red hover:bg-surface-sunken focus-ring shrink-0">
                      <Plus size={14} />
                    </button>
                  )}
                  {dayEntries.length === 0 && !canManage && <span className="text-text-sub self-center">勤務なし</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {editContext && (
        <EntryEditorModal day={editContext.day} entry={editContext.entry} levels={levels} loading={saveMut.isPending}
          onClose={() => setEditContext(null)} onSave={applyEntry}
          onRemove={editContext.entry ? removeEntry : null} />
      )}
    </div>
  );
}

// v0.4 追加要件⑫ 増分⑥ (D-384、-detail.md §3-5): テンプレートの一括再展開。
type ReexpandPersonDiff = {
  employee_id: string | null; external_person_id: string | null; display_name: string;
  added: Array<{ day_of_week: number; start_time: string; end_time: string; preference_level_id: string }>;
  removed: Array<{ day_of_week: number; start_time: string; end_time: string }>;
};
type ReexpandDiffSummary = { changed_person_count: number; persons: ReexpandPersonDiff[] };
type ReexpandContractedImpact = {
  employee_id: string; before: number | null; after: number | null;
  flex_slots_before: number | null; flex_slots_after: number | null;
};
type ReexpandStaffingImpact = {
  day_of_week: number; representative_date: string | null;
  satisfied_before: boolean | null; satisfied_after: boolean | null;
};
type ReexpandImpactSummary = { contracted: ReexpandContractedImpact[]; staffing_by_dow: ReexpandStaffingImpact[] };
type AffectedNode = {
  node_id: string; node_label: string;
  // ⚠️ security-reviewer指摘反映: shift_preference:view の無い店舗では null (バックエンドが伏せる)
  affected_person_count: number | null; conflicted_person_count: number | null;
  actor_can_view: boolean; actor_can_apply: boolean;
  diff_summary?: ReexpandDiffSummary; impact_summary?: ReexpandImpactSummary;
};
type ReexpandPreviewResponse = {
  template_id: string; affected_nodes: AffectedNode[];
  truncated: boolean; total_affected_node_count: number;
};
type ReexpandSkipReason = 'permission_denied' | 'stale_preview' | 'no_approvers' | 'processing_failed';
type ReexpandApplyResult = {
  template_id: string;
  applied: Array<{ node_id: string; status: 'pending_approval' | 'applied'; approval_request_id?: string }>;
  skipped: Array<{ node_id: string; reason: ReexpandSkipReason }>;
};
const REEXPAND_SKIP_REASON_LABELS: Record<ReexpandSkipReason, string> = {
  stale_preview: '確認後に内容が変わったため見送りました。もう一度「一括再展開」をやり直してください',
  permission_denied: '権限が無いため見送りました',
  no_approvers: '承認者が設定されていないため見送りました。管理者へご連絡ください',
  processing_failed: '対象データの変更と競合したため見送りました。もう一度「一括再展開」をやり直してください',
};

/** ApprovalsPage.tsx の satisfiedLabel と同じ表示規約 (代表日が選定できず判定不能な場合は
 *  null を「充足」と誤表示しない、design-expert/design-and-code レビュー指摘の再発防止)。
 *  各ファイルに小さな presentational 関数として持つ (SatisfiedLabel/satisfiedLabel と同じ判断)。 */
function reexpandSatisfiedLabel(satisfied: boolean | null): { text: string; className: string } {
  if (satisfied === null) return { text: '判定不能', className: 'text-text-sub' };
  if (satisfied === false) return { text: '不足', className: 'text-alert-text' };
  return { text: '充足', className: '' };
}

function ReexpandModal({ template, onClose }: { template: Template; onClose: () => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ReexpandApplyResult | null>(null);

  const { data, isLoading, isError } = useQuery<ReexpandPreviewResponse>({
    queryKey: ['standing-pattern-templates', template.template_id, 'reexpand-preview'],
    queryFn: () => apiFetch(`/standing-pattern-templates/${template.template_id}/reexpand-preview`, { method: 'POST' }),
  });
  const affectedNodes = useMemo(() => data?.affected_nodes ?? [], [data]);

  // 初回ロード時、閲覧・適用の両方が可能な店舗を既定で全選択にする (D-384「N店舗・M名に影響します」
  // を確認しやすくする既定値、選択自体はユーザーが後から外せる)。
  useEffect(() => {
    if (!data) return;
    setSelected(new Set(affectedNodes.filter((n) => n.actor_can_view && n.actor_can_apply).map((n) => n.node_id)));
  }, [data, affectedNodes]);

  const applyMut = useMutation({
    // ⚠️ security-reviewer指摘反映: diff_summary はバックエンドで必須化された (省略するとD-385の
    // staleness再検証がスキップされてしまうため)。選択可能なノードは actor_can_view=true のみ
    // (下のチェックボックスで保証済み) なので diff_summary は常に存在するが、念のため無いものは除外する。
    mutationFn: () => apiFetch<ReexpandApplyResult>(`/standing-pattern-templates/${template.template_id}/reexpand-apply`, {
      method: 'POST',
      body: JSON.stringify({
        nodes: [...selected]
          .map((nodeId) => affectedNodes.find((n) => n.node_id === nodeId))
          .filter((n): n is AffectedNode & { diff_summary: ReexpandDiffSummary } => !!n?.diff_summary)
          .map((n) => ({ node_id: n.node_id, diff_summary: n.diff_summary })),
      }),
    }),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ['standing-patterns'] });
      const appliedCount = res.applied.filter((a) => a.status === 'applied').length;
      const pendingCount = res.applied.filter((a) => a.status === 'pending_approval').length;
      toast.success(`即時適用 ${appliedCount}店舗・承認待ち ${pendingCount}店舗${res.skipped.length > 0 ? `・見送り ${res.skipped.length}店舗` : ''}`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '適用に失敗しました'),
  });

  const toggleSelected = (nodeId: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });
  const toggleExpanded = (nodeId: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });

  const selectableCount = affectedNodes.filter((n) => n.actor_can_view && n.actor_can_apply).length;

  return (
    <Modal open title={`「${template.label}」の一括再展開`} onClose={onClose} size="lg"
      footer={result ? (
        <Button variant="primary" onClick={onClose}>閉じる</Button>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose} disabled={applyMut.isPending}>キャンセル</Button>
          <Button variant="primary" disabled={selected.size === 0 || applyMut.isPending || isLoading}
            loading={applyMut.isPending} onClick={() => applyMut.mutate()}>
            選択した{selected.size}店舗に適用
          </Button>
        </>
      )}>
      {result ? (
        <div className="space-y-3">
          <p className="text-sm text-text-body">
            {result.applied.filter((a) => a.status === 'applied').length}店舗へ即時適用、
            {result.applied.filter((a) => a.status === 'pending_approval').length}店舗が承認待ちになりました。
            {result.skipped.length > 0 && ` ${result.skipped.length}店舗は見送られました。`}
          </p>
          {result.skipped.length > 0 && (
            <ul className="space-y-1">
              {result.skipped.map((s) => (
                <li key={s.node_id} className="text-xs text-danger-text">
                  {affectedNodes.find((n) => n.node_id === s.node_id)?.node_label ?? s.node_id}: {REEXPAND_SKIP_REASON_LABELS[s.reason]}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : isLoading ? (
        <div className="text-text-sub text-sm">読み込み中…</div>
      ) : isError ? (
        <div className="text-danger-text text-sm">影響範囲の取得に失敗しました</div>
      ) : affectedNodes.length === 0 ? (
        <EmptyState message="このテンプレートはまだどの店舗にも展開されていません" />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-sub">
            {affectedNodes.length}店舗・{affectedNodes.reduce((sum, n) => sum + (n.affected_person_count ?? 0), 0)}名に影響します。
          </p>
          {data?.truncated && (
            <p className="text-xs text-alert-text">
              このテンプレートは{data.total_affected_node_count}店舗に展開されていますが、
              一度に確認できるのは{affectedNodes.length}店舗までです。今回の分を適用後、もう一度「一括再展開」を実行してください。
            </p>
          )}
          <ul className="space-y-2 max-h-[28rem] overflow-y-auto">
            {affectedNodes.map((n) => {
              const canSelect = n.actor_can_view && n.actor_can_apply;
              const isSelected = selected.has(n.node_id);
              const isExpanded = expanded.has(n.node_id);
              return (
                <li key={n.node_id} className="border border-surface-border rounded p-2">
                  <div className="flex items-start gap-2">
                    {/* design-expertレビュー指摘 (Critical): 20px (w-5 h-5) は44px未満でタップ領域不足。
                        DataBrowsePage.tsx の既存パターン (label でラップし44×44を確保) に揃える。 */}
                    <label className="inline-flex items-center justify-center w-11 h-11 -m-3 shrink-0 cursor-pointer has-[:disabled]:cursor-not-allowed">
                      <input type="checkbox" className="w-5 h-5" checked={isSelected} disabled={!canSelect}
                        onChange={() => toggleSelected(n.node_id)} aria-label={`${n.node_label}に適用`} />
                    </label>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-sm font-medium text-text-body">{n.node_label}</span>
                        <span className="text-xs text-text-sub">{n.affected_person_count == null ? '—' : `${n.affected_person_count}名`}</span>
                      </div>
                      {!n.actor_can_view && <p className="text-xs text-text-sub mt-0.5">この店舗の変更内容を閲覧する権限がありません</p>}
                      {n.actor_can_view && !n.actor_can_apply && <p className="text-xs text-text-sub mt-0.5">この店舗に適用する権限がありません</p>}
                      {n.actor_can_view && !!n.conflicted_person_count && (
                        <p className="text-xs text-alert-text mt-0.5">
                          {n.conflicted_person_count}名は手入力の枠と重複するため今回の再展開の対象外です。個別に調整してください
                        </p>
                      )}
                      {n.diff_summary && (
                        <button type="button"
                          className="text-xs text-misespark-navy underline mt-1 min-h-[44px] sm:min-h-0 inline-flex items-center"
                          onClick={() => toggleExpanded(n.node_id)}>
                          {isExpanded ? '差分を隠す' : `差分を見る (${n.diff_summary.changed_person_count}名)`}
                        </button>
                      )}
                      {isExpanded && n.diff_summary && (
                        <div className="mt-2 space-y-2 border-t border-surface-line pt-2">
                          {n.diff_summary.persons.length === 0 ? (
                            <p className="text-xs text-text-sub">変更点はありません。</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {n.diff_summary.persons.map((p) => (
                                <li key={p.employee_id ? `E:${p.employee_id}` : `X:${p.external_person_id ?? ''}`}
                                  className="border border-surface-line rounded p-2 bg-surface-sunken">
                                  <p className="font-medium text-text-body text-sm">{p.display_name}</p>
                                  {p.added.length > 0 && (
                                    <p className="text-xs text-text-body mt-0.5">
                                      追加: {p.added.map((a, i) => (
                                        <span key={i}>{i > 0 && '、'}{DOW_LABELS[a.day_of_week]}曜日 {a.start_time}〜{a.end_time}</span>
                                      ))}
                                    </p>
                                  )}
                                  {p.removed.length > 0 && (
                                    <p className="text-xs text-text-sub mt-0.5">
                                      削除: {p.removed.map((r, i) => (
                                        <span key={i}>{i > 0 && '、'}{DOW_LABELS[r.day_of_week]}曜日 {r.start_time}〜{r.end_time}</span>
                                      ))}
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                          {n.impact_summary && n.impact_summary.staffing_by_dow.length > 0 && (
                            <div className="space-y-1 pt-1 border-t border-surface-border">
                              <p className="text-xs font-semibold text-text-sub">必要人員への影響 (代表日での近似)</p>
                              <ul className="space-y-0.5">
                                {n.impact_summary.staffing_by_dow.map((d) => {
                                  const before = reexpandSatisfiedLabel(d.satisfied_before);
                                  const after = reexpandSatisfiedLabel(d.satisfied_after);
                                  return (
                                    <li key={d.day_of_week} className="text-xs text-text-body">
                                      {DOW_LABELS[d.day_of_week]}曜日:{' '}
                                      <span className={before.className}>{before.text}</span>
                                      {' → '}
                                      <span className={`${after.className} ${d.satisfied_after === false ? 'font-semibold' : ''}`}>{after.text}</span>
                                      {d.representative_date && <span className="text-text-sub"> (代表日 {d.representative_date})</span>}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {selectableCount === 0 && (
            <p className="text-xs text-alert-text">適用できる店舗がありません (閲覧・適用いずれかの権限が必要です)。</p>
          )}
        </div>
      )}
    </Modal>
  );
}

export function StandingPatternTemplatesSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasPermission(user?.effective_permissions, 'tenant_config', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ template_id: string | null; code: string; label: string; node_id: string; sort_order: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [entriesFor, setEntriesFor] = useState<string | null>(null);
  const [reexpandTarget, setReexpandTarget] = useState<Template | null>(null);

  const { data, isLoading } = useQuery<{ templates: Template[] }>({
    queryKey: ['standing-pattern-templates'], queryFn: () => apiFetch('/standing-pattern-templates'),
  });
  const templates = data?.templates ?? [];
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const nodeLabelById = useMemo(() => new Map(flatNodes.map(({ node }) => [node.node_id, node.name])), [flatNodes]);

  const saveMut = useMutation({
    mutationFn: (d: NonNullable<typeof draft>) => {
      const payload = {
        code: d.code.trim(), label: d.label.trim(),
        node_id: d.node_id === NULL_NODE_VALUE ? null : d.node_id,
        sort_order: Number(d.sort_order) || 0,
      };
      return d.template_id
        ? apiFetch(`/standing-pattern-templates/${d.template_id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : apiFetch('/standing-pattern-templates', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['standing-pattern-templates'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/standing-pattern-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['standing-pattern-templates'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-3 max-w-3xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-text-body">恒常パターンテンプレート</h2>
          <p className="text-sm text-text-sub mt-1">
            「平日フルタイム」「土日のみ」等、曜日ごとの標準的な勤務時刻をまとめて登録します。
            店舗の「恒常パターン調整」画面から、社員1人ずつに展開できます。
          </p>
        </div>
        {canManage && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({ template_id: null, code: '', label: '', node_id: NULL_NODE_VALUE, sort_order: String(templates.length) })}>
            テンプレートを追加
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : templates.length === 0 ? (
        <EmptyState message="恒常パターンテンプレートがまだありません" description={canManage ? '「テンプレートを追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {templates.map((t) => (
            <li key={t.template_id} className="border border-surface-border rounded-lg bg-white p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-text-body break-words">{t.label}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">{t.code}</span>
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
                    {t.node_id ? (nodeLabelById.get(t.node_id) ?? '(削除済みノード)') : 'テナント全体'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" leftIcon={<Clock size={14} />}
                    aria-expanded={entriesFor === t.template_id}
                    onClick={() => setEntriesFor(entriesFor === t.template_id ? null : t.template_id)}>
                    {canManage ? '曜日別時刻を設定' : '曜日別時刻を確認'}
                  </Button>
                  {canManage && (
                    <>
                      {/* v0.4 追加要件⑫ 増分⑥ (D-384): テンプレートの内容を直した後、既に展開済みの
                          店舗へ「差分プレビュー→承認」経路で再展開する。 */}
                      <button type="button" aria-label="一括再展開" title="一括再展開"
                        onClick={() => setReexpandTarget(t)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><RefreshCw size={14} /></button>
                      <button type="button" aria-label="編集"
                        onClick={() => setDraft({ template_id: t.template_id, code: t.code, label: t.label, node_id: t.node_id ?? NULL_NODE_VALUE, sort_order: String(t.sort_order) })}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                      <button type="button" aria-label="削除" onClick={() => setDeleteTarget(t)}
                        className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
              {entriesFor === t.template_id && (
                <EntriesPanel templateId={t.template_id} canManage={canManage} />
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.template_id ? 'テンプレートを編集' : 'テンプレートを追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50} className={inputCls} placeholder="例: weekday_fulltime" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={200} className={inputCls} placeholder="例: 平日フルタイム" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">適用範囲</label>
              <Select value={draft.node_id} onValueChange={(v) => setDraft({ ...draft, node_id: v })}>
                <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NULL_NODE_VALUE}>テナント全体</SelectItem>
                  {flatNodes.map(({ node, depth }) => (
                    <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-text-sub mt-1">テナント全体を選ぶとどの店舗からでも使えます。特定の店舗を選ぶと、その配下の店舗からのみ使えます。</p>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="テンプレートを削除"
        message={`「${deleteTarget?.label}」を削除します。曜日別時刻も同時に削除されます。展開済みの店舗が残っている場合は削除できません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.template_id)} />

      {reexpandTarget && (
        <ReexpandModal template={reexpandTarget} onClose={() => setReexpandTarget(null)} />
      )}
    </div>
  );
}
